import { useState, useEffect } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Loader2, Mic, ArrowRight, ArrowLeft, Link2 } from 'lucide-react';
import PublicFaq from '../../components/PublicFaq';
import { LogoOrMark } from '../../components/Lettermark';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SpotifyIcon = (props) => (
  <svg viewBox="0 0 24 24" fill="currentColor" {...props}><path d="M12 0C5.4 0 0 5.4 0 12s5.4 12 12 12 12-5.4 12-12S18.66 0 12 0zm5.5 17.3a.75.75 0 0 1-1.03.25c-2.82-1.72-6.37-2.11-10.55-1.16a.75.75 0 1 1-.33-1.46c4.57-1.04 8.5-.59 11.66 1.34.35.22.46.68.25 1.03zm1.47-3.27a.94.94 0 0 1-1.29.31c-3.23-1.98-8.15-2.56-11.97-1.4a.94.94 0 1 1-.55-1.8c4.37-1.32 9.79-.68 13.5 1.6.44.27.58.85.31 1.29zm.13-3.4C15.8 8.3 8.5 8.06 4.9 9.16a1.12 1.12 0 1 1-.65-2.15c4.13-1.25 12.2-1 16.55 1.58a1.13 1.13 0 0 1-1.17 1.93z" /></svg>
);
const AppleIcon = (props) => (
  <svg viewBox="0 0 24 24" fill="currentColor" {...props}><path d="M12 1.5A10.5 10.5 0 0 0 1.5 12c0 4.9 3.35 9.02 7.88 10.18.02-.35.02-.85 0-1.3-.02-.4-.02-1.02 0-1.5A3.62 3.62 0 0 1 8.4 16.2a3.6 3.6 0 1 1 7.2 0c0 .9-.34 1.72-.98 2.35v.02c-.02.5-.02 1.14 0 1.55.02.44.02.92 0 1.26A10.5 10.5 0 0 0 12 1.5zm0 6.4a2.6 2.6 0 1 0 0 5.2 2.6 2.6 0 0 0 0-5.2zm0 1.5a1.1 1.1 0 1 1 0 2.2 1.1 1.1 0 0 1 0-2.2z" /></svg>
);

const ListenLinks = ({ spotify, apple, size = 'md' }) => {
  const cls = size === 'lg' ? 'px-5 py-3 text-sm' : 'px-4 py-2 text-xs';
  return (
    <div className="flex flex-wrap gap-3">
      {spotify && (
        <a href={spotify} target="_blank" rel="noreferrer" data-testid="listen-spotify"
          className={`inline-flex items-center gap-2 rounded-full font-semibold bg-[#1DB954] hover:bg-[#1aa34a] text-white transition-colors ${cls}`}>
          <SpotifyIcon className="w-4 h-4" /> Listen on Spotify
        </a>
      )}
      {apple && (
        <a href={apple} target="_blank" rel="noreferrer" data-testid="listen-apple"
          className={`inline-flex items-center gap-2 rounded-full font-semibold bg-[#1A3A5F] hover:bg-[#12293f] text-white transition-colors ${cls}`}>
          <AppleIcon className="w-4 h-4" /> Listen on Apple Podcasts
        </a>
      )}
    </div>
  );
};

const Podcast = () => {
  const [episodes, setEpisodes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sp, setSp] = useSearchParams();
  const cat = sp.get('category');
  const setCat = (c) => {
    const next = new URLSearchParams(sp);
    if (c) next.set('category', c); else next.delete('category');
    setSp(next);
  };

  useEffect(() => {
    axios.get(`${API}/public/podcast`)
      .then(({ data }) => setEpisodes(data.episodes || []))
      .catch(() => setEpisodes([]))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    const origTitle = document.title;
    const title = cat
      ? `${cat} podcasts | Care Tech Compare`
      : 'The Care Tech Compare Podcast | Conversations with care tech suppliers';
    const desc = cat
      ? `Listen to Care Tech Compare podcast episodes featuring ${cat} suppliers - founders and teams behind the care technology transforming UK social care.`
      : 'The Care Tech Compare podcast - conversations with the founders and teams behind the care technology transforming UK social care.';
    const url = `${window.location.origin}/podcast${cat ? `?category=${encodeURIComponent(cat)}` : ''}`;
    const image = `${window.location.origin}/brand/podcast-share-wide.jpg`;
    document.title = title;
    const tags = [
      { name: 'description', content: desc },
      { property: 'og:type', content: 'website' },
      { property: 'og:title', content: title },
      { property: 'og:description', content: desc },
      { property: 'og:url', content: url },
      { property: 'og:image', content: image },
      { name: 'twitter:card', content: 'summary_large_image' },
      { name: 'twitter:title', content: title },
      { name: 'twitter:description', content: desc },
      { name: 'twitter:image', content: image },
    ];
    const created = [];
    tags.forEach(({ name, property, content }) => {
      const sel = name ? `meta[name="${name}"]` : `meta[property="${property}"]`;
      let el = document.head.querySelector(sel);
      let isNew = false;
      if (!el) {
        el = document.createElement('meta');
        if (name) el.setAttribute('name', name); else el.setAttribute('property', property);
        document.head.appendChild(el);
        isNew = true;
      }
      created.push({ el, isNew, prev: el.getAttribute('content') });
      el.setAttribute('content', content);
    });
    return () => {
      document.title = origTitle;
      created.forEach(({ el, isNew, prev }) => {
        if (isNew) el.remove();
        else if (prev !== null) el.setAttribute('content', prev);
      });
    };
  }, [cat]);

  const allCats = [...new Set(episodes.flatMap((e) => e.categories || []))].sort();
  const shown = cat ? episodes.filter((e) => (e.categories || []).includes(cat)) : episodes;

  const copyShareLink = async () => {
    const url = `${API}/public/share/podcast${cat ? `?category=${encodeURIComponent(cat)}` : ''}`;
    try {
      await navigator.clipboard.writeText(url);
      toast.success(cat ? `Rich-preview link for ${cat} copied` : 'Rich-preview link copied');
    } catch {
      toast.error('Could not copy the link');
    }
  };

  return (
    <div data-testid="podcast-page">
      <div className="bg-[#1A3A5F] text-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-14">
          <div className="inline-flex items-center gap-2 text-[#2fd0c6] font-semibold text-sm mb-3"><Mic className="w-4 h-4" /> The Care Tech Compare Podcast</div>
          <h1 className="font-heading text-4xl sm:text-5xl font-bold tracking-tight">{cat ? `${cat} podcasts` : 'Conversations with care tech suppliers'}</h1>
          <p className="text-white/70 mt-3 max-w-2xl text-lg">{cat ? `Hear from ${cat} suppliers on the Care Tech Compare podcast - founders and teams behind the tools transforming social care.` : 'Hear directly from the founders and teams behind the tools transforming social care. New episodes featuring our partners.'}</p>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {!loading && allCats.length > 0 && (
          <div className="flex items-start justify-between gap-4 mb-8 flex-wrap">
            <div className="flex flex-wrap gap-2" data-testid="podcast-category-filter">
              <button onClick={() => setCat(null)} className={`text-sm font-semibold px-3.5 py-1.5 rounded-full border transition-colors ${!cat ? 'bg-[#1A3A5F] text-white border-[#1A3A5F]' : 'text-gray-600 border-[#E5E7EB] hover:border-[#00A79D]'}`} data-testid="podcast-cat-all">All episodes</button>
              {allCats.map((c) => (
                <button key={c} onClick={() => setCat(c)} className={`text-sm font-semibold px-3.5 py-1.5 rounded-full border transition-colors ${cat === c ? 'bg-[#00A79D] text-white border-[#00A79D]' : 'text-gray-600 border-[#E5E7EB] hover:border-[#00A79D]'}`} data-testid={`podcast-cat-${c.replace(/[^a-zA-Z0-9]+/g, '-').toLowerCase().replace(/^-|-$/g, '')}`}>{c}</button>
              ))}
            </div>
            <button onClick={copyShareLink} className="inline-flex items-center gap-1.5 text-sm font-semibold text-[#00857D] hover:text-[#00A79D] shrink-0 whitespace-nowrap" data-testid="podcast-share-link"><Link2 className="w-4 h-4" /> Share {cat || 'podcast'}</button>
          </div>
        )}
        {loading ? (
          <div className="flex justify-center py-20"><Loader2 className="w-6 h-6 animate-spin text-[#00A79D]" /></div>
        ) : shown.length === 0 ? (
          <div className="text-center py-20" data-testid="podcast-empty">
            <Mic className="w-10 h-10 text-gray-300 mx-auto mb-3" />
            <p className="text-gray-500">{cat ? `No episodes in ${cat} yet.` : 'Episodes are on the way - check back soon.'}</p>
            {cat ? <button onClick={() => setCat(null)} className="inline-flex items-center gap-1 text-[#00A79D] font-semibold mt-3">Show all episodes</button>
              : <Link to="/browse" className="inline-flex items-center gap-1 text-[#00A79D] font-semibold mt-3">Browse suppliers <ArrowRight className="w-4 h-4" /></Link>}
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 gap-6">
            {shown.map((ep) => (
              <Link key={ep.slug} to={ep.post_url} className="border border-[#E5E7EB] rounded-xl p-5 bg-white hover:border-[#00A79D] hover:shadow-sm transition-all flex flex-col" data-testid={`podcast-episode-${ep.slug}`}>
                <div className="flex items-center gap-3 mb-3">
                  <LogoOrMark logo={ep.logo} name={ep.supplier_name} imgClassName="h-9 w-auto max-w-[120px] object-contain" markClassName="h-9 w-9 text-sm rounded-md" />
                  <span className="text-sm font-medium text-gray-500">{ep.supplier_name}</span>
                </div>
                <h2 className="font-heading text-lg font-bold text-gray-900">{ep.title}</h2>
                {ep.summary && <p className="text-sm text-gray-500 mt-1 line-clamp-3 flex-1">{ep.summary}</p>}
                {(ep.categories || []).length > 0 && (
                  <div className="flex flex-wrap gap-1.5 mt-3" data-testid={`podcast-cats-${ep.slug}`}>
                    {ep.categories.map((c) => <span key={c} className="text-[11px] text-[#00857D] bg-[#00A79D]/8 border border-[#00A79D]/20 px-2 py-0.5 rounded-full">{c}</span>)}
                  </div>
                )}
                <span className="inline-flex items-center gap-1 text-sm text-[#00A79D] font-semibold mt-4">Read &amp; listen <ArrowRight className="w-3.5 h-3.5" /></span>
              </Link>
            ))}
          </div>
        )}
      </div>
      <PublicFaq id="podcast" heading="About the podcast"
        intro="Honest conversations with care technology founders and social care leaders."
        faqs={[
          { q: 'What is the Care Tech Compare podcast about?', a: 'It goes behind the marketing to hear directly from the people building and using care technology - founders, product leaders and social care professionals. Expect candid stories about what actually works in real services, the mistakes to avoid, and where care tech is heading.' },
          { q: 'Who should listen?', a: 'Care providers weighing up new systems, suppliers who want to understand what buyers really care about, and anyone interested in how technology is changing social care. Episodes are practical rather than salesy, so there is something useful whichever side of the sector you are on.' },
          { q: 'How often are new episodes released, and where can I listen?', a: 'New episodes are published regularly and you can listen right here on the site, with each episode also written up so you can read the highlights. Browse by category to find the topics most relevant to your service.' },
          { q: 'Can I suggest a guest or topic?', a: 'Absolutely. If there is a care technology story you think deserves airtime, or a challenge you would like explored, get in touch via our contact page - many episodes come from listener suggestions.' },
        ]} />

    </div>
  );
};

export const PodcastPost = () => {
  const { slug } = useParams();
  const [ep, setEp] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    setLoading(true);
    axios.get(`${API}/public/podcast/${slug}`)
      .then(({ data }) => setEp(data))
      .catch(() => setNotFound(true))
      .finally(() => setLoading(false));
  }, [slug]);

  useEffect(() => {
    if (!ep) return;
    const origTitle = document.title;
    const title = `${ep.supplier_name} on the Care Tech Compare Podcast`;
    const desc = ep.summary || `Hear from ${ep.supplier_name} on the Care Tech Compare podcast.`;
    const url = `${window.location.origin}/podcast/${slug}`;
    const image = `${API}/public/share/podcast/image/${slug}.jpg?variant=wide`;
    document.title = title;
    const tags = [
      { name: 'description', content: desc },
      { property: 'og:type', content: 'article' },
      { property: 'og:title', content: title },
      { property: 'og:description', content: desc },
      { property: 'og:url', content: url },
      { property: 'og:image', content: image },
      { name: 'twitter:card', content: 'summary_large_image' },
      { name: 'twitter:title', content: title },
      { name: 'twitter:description', content: desc },
      { name: 'twitter:image', content: image },
    ];
    const created = [];
    tags.forEach(({ name, property, content }) => {
      const sel = name ? `meta[name="${name}"]` : `meta[property="${property}"]`;
      let el = document.head.querySelector(sel);
      let isNew = false;
      if (!el) {
        el = document.createElement('meta');
        if (name) el.setAttribute('name', name); else el.setAttribute('property', property);
        document.head.appendChild(el); isNew = true;
      }
      created.push({ el, isNew, prev: el.getAttribute('content') });
      el.setAttribute('content', content);
    });
    return () => {
      document.title = origTitle;
      created.forEach(({ el, isNew, prev }) => { if (isNew) el.remove(); else if (prev !== null) el.setAttribute('content', prev); });
    };
  }, [ep, slug]);

  const shareEpisode = async () => {
    try {
      await navigator.clipboard.writeText(`${API}/public/share/podcast/episode/${slug}`);
      toast.success('Shareable episode link copied');
    } catch {
      toast.error('Could not copy the link');
    }
  };

  if (loading) return <div className="flex justify-center py-32"><Loader2 className="w-6 h-6 animate-spin text-[#00A79D]" /></div>;
  if (notFound || !ep) return (
    <div className="max-w-3xl mx-auto px-4 py-32 text-center">
      <p className="text-gray-500">This podcast episode could not be found.</p>
      <Link to="/podcast" className="inline-flex items-center gap-1 text-[#00A79D] font-semibold mt-3"><ArrowLeft className="w-4 h-4" /> All episodes</Link>
    </div>
  );

  return (
    <div data-testid="podcast-post-page">
      <div className="bg-[#1A3A5F] text-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="flex items-center justify-between gap-4 mb-6">
            <Link to="/podcast" className="text-sm text-white/60 hover:text-white inline-flex items-center gap-1.5"><ArrowLeft className="w-4 h-4" /> All episodes</Link>
            <button onClick={shareEpisode} className="inline-flex items-center gap-1.5 text-sm font-semibold text-[#2fd0c6] hover:text-white transition-colors" data-testid="podcast-episode-share"><Link2 className="w-4 h-4" /> Share episode</button>
          </div>
          <div className="inline-flex items-center gap-2 text-[#2fd0c6] font-semibold text-sm mb-3"><Mic className="w-4 h-4" /> The Care Tech Compare Podcast</div>
          <h1 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight">{ep.title}</h1>
          <Link to={`/suppliers/${ep.supplier_slug}`} className="inline-flex items-center gap-2 mt-4 text-white/80 hover:text-white" data-testid="podcast-post-supplier">
            <LogoOrMark logo={ep.logo} name={ep.supplier_name} imgClassName="h-8 w-auto max-w-[110px] object-contain bg-white rounded px-1" markClassName="h-8 w-8 text-sm rounded-md" />
            <span className="font-medium">{ep.supplier_name}</span>
          </Link>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <ListenLinks spotify={ep.spotify_url} apple={ep.apple_url} size="lg" />
        {ep.summary && <p className="text-gray-700 leading-relaxed mt-8 text-lg">{ep.summary}</p>}
        {ep.spotify_embed && (
          <div className="mt-8">
            <iframe title={ep.title} src={ep.spotify_embed} width="100%" height="232" frameBorder="0" allow="clipboard-write; encrypted-media; fullscreen; picture-in-picture" loading="lazy" className="rounded-xl" />
          </div>
        )}
        <div className="mt-10 border-t border-[#E5E7EB] pt-6">
          <Link to={`/suppliers/${ep.supplier_slug}`} className="inline-flex items-center gap-1.5 bg-[#00A79D] hover:bg-[#00857D] text-white font-semibold px-5 py-2.5 rounded-md transition-colors" data-testid="podcast-post-view-supplier">
            View {ep.supplier_name} <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </div>
    </div>
  );
};

export default Podcast;
